import { oc } from "@orpc/contract";
import {
  CorrectionStrategy,
  ValidationRuleStatus,
  ValidationRuleTrigger,
  ValidationRuleType,
  ValidationRunStatus,
  ValidationViolationStatus,
} from "@erp/shared-types";
import { z } from "zod";
import { CatalogAdvancedFilterSchema } from "./products.contract";

const UUIDSchema = z.uuid();

// ─── Validation rules ─────────────────────────────────────────────────────────
// A rule declares an invariant over one logical AS/400 ingest schema (e.g.
// `as_item`): group its rows by `groupingFields`, then require `validatedFields`
// to agree within each group. Authoring, activating, and running a rule are all
// org-scoped; the catalog rows themselves live in the ingest database, so a rule
// names its schema and fields by *name*, never by id.

/**
 * How two cell values are compared before the rule calls them different. All
 * three are applied before comparison, never to the stored data.
 */
export const ValidationNormalizationSchema = z.object({
  /** Compare `" ACME "` equal to `"ACME"`. */
  trimWhitespace: z.boolean(),
  /** Compare `"acme"` equal to `"ACME"`. */
  caseInsensitive: z.boolean(),
  /** Treat an empty string as a missing value, so `""` equals NULL. */
  blankEqualsNull: z.boolean(),
});

/** The normalization a rule gets when the author doesn't say otherwise. */
export const DEFAULT_VALIDATION_NORMALIZATION = {
  trimWhitespace: true,
  caseInsensitive: false,
  blankEqualsNull: true,
} as const;

/** Write form of {@link ValidationNormalizationSchema} — every flag optional. */
export const ValidationNormalizationInputSchema = z.object({
  trimWhitespace: z
    .boolean()
    .default(DEFAULT_VALIDATION_NORMALIZATION.trimWhitespace),
  caseInsensitive: z
    .boolean()
    .default(DEFAULT_VALIDATION_NORMALIZATION.caseInsensitive),
  blankEqualsNull: z
    .boolean()
    .default(DEFAULT_VALIDATION_NORMALIZATION.blankEqualsNull),
});

/**
 * Parameters of the chosen {@link CorrectionStrategy}. Only
 * `preferred_source` takes one: the division (`core_data_sources.id`) whose
 * value wins a disagreement.
 */
export const ValidationStrategyConfigSchema = z.object({
  preferredDataSourceId: UUIDSchema.optional(),
});

/** What one run measured. Written once, when the run finishes. */
export const ValidationRunStatsSchema = z.object({
  /** Ingest rows read. */
  recordsEvaluated: z.number().int().nonnegative(),
  /** Distinct groups the rows fell into. */
  groupsEvaluated: z.number().int().nonnegative(),
  /** Groups where at least one validated field disagreed. */
  violatingGroups: z.number().int().nonnegative(),
  /** Violations the strategy could propose a value for. */
  correctionsProposed: z.number().int().nonnegative(),
  /** Violations it could not — e.g. a tie under `most_common`. */
  ambiguousGroups: z.number().int().nonnegative(),
});

/** A validation rule as read back: everything the detail page edits. */
export const ValidationRuleSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Logical ingest schema name, e.g. `as_item`. */
  schemaName: z.string(),
  ruleType: z.nativeEnum(ValidationRuleType),
  /** Null means "every row of the schema". */
  rowFilters: z.array(CatalogAdvancedFilterSchema).nullable(),
  groupingFields: z.array(z.string()),
  validatedFields: z.array(z.string()),
  normalization: ValidationNormalizationSchema,
  correctionStrategy: z.nativeEnum(CorrectionStrategy),
  strategyConfig: ValidationStrategyConfigSchema.nullable(),
  assigneeUserId: UUIDSchema.nullable(),
  status: z.nativeEnum(ValidationRuleStatus),
  executionTrigger: z.nativeEnum(ValidationRuleTrigger),
  scheduleCron: z.string().nullable(),
  createdByUserId: UUIDSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * The rule's last run, as far as a list row cares: when it started and how it
 * ended. Null when the rule has never run.
 */
export const ValidationRuleLastRunSchema = z.object({
  /** ISO timestamp the run started. */
  time: z.string(),
  status: z.nativeEnum(ValidationRunStatus),
});

/**
 * One row of the rules list. The three aggregates are computed server-side in
 * grouped queries so the list page never fans out one request per rule.
 */
export const ValidationRuleListItemSchema = ValidationRuleSchema.extend({
  lastRun: ValidationRuleLastRunSchema.nullable(),
  /** Violations of this rule still in `open`. */
  openViolationCount: z.number().int().nonnegative(),
  /** Distinct still-draft reviews this rule's runs staged corrections into. */
  openReviewCount: z.number().int().nonnegative(),
});

export const ListValidationRulesInputSchema = z.object({
  status: z.nativeEnum(ValidationRuleStatus).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const ListValidationRulesResponseSchema = z.object({
  rules: z.array(ValidationRuleListItemSchema),
  /** Rules matching the filter, ignoring paging. */
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

export const GetValidationRuleInputSchema = z.object({ id: UUIDSchema });

export const GetValidationRuleResponseSchema = z.object({
  rule: ValidationRuleSchema.nullable(),
});

export const CreateValidationRuleInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).nullable().optional(),
  schemaName: z.string().trim().min(1).max(255),
  ruleType: z
    .nativeEnum(ValidationRuleType)
    .default(ValidationRuleType.FieldConsistency),
  rowFilters: z
    .array(CatalogAdvancedFilterSchema)
    .max(20)
    .nullable()
    .optional(),
  groupingFields: z.array(z.string().trim().min(1)).min(1).max(20),
  validatedFields: z.array(z.string().trim().min(1)).min(1).max(50),
  normalization: ValidationNormalizationInputSchema.default(
    DEFAULT_VALIDATION_NORMALIZATION,
  ),
  // Matches the wizard's default and the model's, so a rule created through the
  // API without a strategy behaves like one created through the UI.
  correctionStrategy: z
    .nativeEnum(CorrectionStrategy)
    .default(CorrectionStrategy.MostCommon),
  strategyConfig: ValidationStrategyConfigSchema.nullable().optional(),
  assigneeUserId: UUIDSchema.nullable().optional(),
  status: z
    .nativeEnum(ValidationRuleStatus)
    .default(ValidationRuleStatus.Draft),
  executionTrigger: z
    .nativeEnum(ValidationRuleTrigger)
    .default(ValidationRuleTrigger.Manual),
  scheduleCron: z.string().trim().max(255).nullable().optional(),
});

export const CreateValidationRuleResponseSchema = z.object({
  rule: ValidationRuleSchema,
});

export const UpdateValidationRuleInputSchema = z
  .object({
    id: UUIDSchema,
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    schemaName: z.string().trim().min(1).max(255).optional(),
    rowFilters: z
      .array(CatalogAdvancedFilterSchema)
      .max(20)
      .nullable()
      .optional(),
    groupingFields: z.array(z.string().trim().min(1)).min(1).max(20).optional(),
    validatedFields: z
      .array(z.string().trim().min(1))
      .min(1)
      .max(50)
      .optional(),
    normalization: ValidationNormalizationInputSchema.optional(),
    correctionStrategy: z.nativeEnum(CorrectionStrategy).optional(),
    strategyConfig: ValidationStrategyConfigSchema.nullable().optional(),
    assigneeUserId: UUIDSchema.nullable().optional(),
    status: z.nativeEnum(ValidationRuleStatus).optional(),
    executionTrigger: z.nativeEnum(ValidationRuleTrigger).optional(),
    scheduleCron: z.string().trim().max(255).nullable().optional(),
  })
  .refine((input) => Object.keys(input).some((field) => field !== "id"), {
    message: "At least one field to update must be provided",
  });

export const UpdateValidationRuleResponseSchema = z.object({
  rule: ValidationRuleSchema,
});

export const DuplicateValidationRuleInputSchema = z.object({ id: UUIDSchema });

export const DuplicateValidationRuleResponseSchema = z.object({
  // The copy — always a Draft named "Copy of <original>".
  rule: ValidationRuleSchema,
});

export const DeleteValidationRuleInputSchema = z.object({ id: UUIDSchema });

export const DeleteValidationRuleResponseSchema = z.object({
  success: z.boolean(),
});

// ─── Schema picker ───────────────────────────────────────────────────────────
// What the rule builder needs to offer a schema and its fields: the org's
// scannable logical schemas (the ones the catalog scan can order by SKU) and
// each one's field list, with the AS/400 company/division/branch keys marked so
// the UI can suggest them as grouping fields.

/** Which AS/400 key ("CDB") role a field plays, if any. */
export const ValidationSchemaFieldRoleSchema = z.enum([
  "company",
  "division",
  "branch",
]);

export const ValidationSchemaFieldSchema = z.object({
  fieldName: z.string(),
  label: z.string(),
  fieldType: z.string(),
  /**
   * Non-null when the field is one of the schema's company/division/branch key
   * columns (see AS400_CDB_COLUMNS_BY_SCHEMA).
   */
  cdbRole: ValidationSchemaFieldRoleSchema.nullable(),
  /** True when the field is the schema's item-number (SKU) column. */
  isSkuColumn: z.boolean(),
});

export const ValidationRuleSchemaSchema = z.object({
  /** `core_schema_definitions.id`. */
  schemaDefinitionId: UUIDSchema,
  /** `core_schema_definitions.name` — what a rule stores as `schemaName`. */
  name: z.string(),
  /** Human label; falls back to the name. */
  label: z.string(),
  /** The schema's item-number column, the scan's sort key. */
  skuColumn: z.string(),
  /** Divisions (`core_data_sources`) with a physical table for this schema. */
  divisionCount: z.number().int().nonnegative(),
  fields: z.array(ValidationSchemaFieldSchema),
});

export const ListValidationRuleSchemasResponseSchema = z.object({
  schemas: z.array(ValidationRuleSchemaSchema),
});

// ─── Evaluation: preview, runs, violations ───────────────────────────────────
// Running a rule is one pass over every division's copy of its schema. The same
// evaluator answers a preview (read-only, capped smaller) and a real run (which
// records a `im_validation_runs` row and reconciles `im_validation_violations`),
// so what an author sees before saving is what the run will report.

/** Grouping field → the normalized value every row of the group shares. */
export const ValidationGroupKeyValuesSchema = z.record(
  z.string(),
  z.string().nullable(),
);

/** One division a value was seen in. */
export const ValidationObservedDivisionSchema = z.object({
  /** `core_data_sources.id`. */
  dataSourceId: UUIDSchema,
  /** The division's AS/400 code, e.g. `RD`. */
  divisionCode: z.string(),
});

/** One distinct value seen for a validated field, and where it was seen. */
export const ValidationObservedValueSchema = z.object({
  /** The normalized value; null when the cell is missing (or blank). */
  value: z.string().nullable(),
  /** Ingest rows carrying it. Exact, even when the sampled rows are capped. */
  rowCount: z.number().int().nonnegative(),
  divisions: z.array(ValidationObservedDivisionSchema),
});

/** What one group's validated field looks like across the divisions. */
export const ValidationObservedValuesSchema = z.object({
  /** The distinct values, ordered most-carried first. */
  values: z.array(ValidationObservedValueSchema),
  /** Every division with a row in the group — the vote denominator. */
  divisions: z.array(ValidationObservedDivisionSchema),
});

/**
 * What the rule's correction strategy made of a disagreement: a value to
 * propose with the sentence explaining it, or the reason none could be picked.
 */
export const ValidationCorrectionOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("proposed"),
    value: z.string(),
    reason: z.string(),
  }),
  z.object({ kind: z.literal("ambiguous"), warning: z.string() }),
]);

/** One ingest row behind a violation — everything a staged edit needs. */
export const ValidationViolationRowSchema = z.object({
  /** `core_data_source_links.id` — the row's division table. */
  dataSourceLinkId: UUIDSchema,
  /** The ingest row's surrogate id, i.e. its `data_id`. */
  dataId: z.string(),
  divisionCode: z.string(),
  /**
   * The row's location code (`iloc`/`ploc`/… per schema), trimmed. Null when
   * the cell is blank or the schema keeps no location; absent on details
   * stored before locations were recorded.
   */
  location: z.string().nullable().optional(),
  /** The cell as the ingest table holds it — the drift baseline for an edit. */
  rawValue: z.string().nullable(),
  /** …and as the rule compared it. */
  normalizedValue: z.string().nullable(),
});

/**
 * The disagreement itself, exactly as it is stored in
 * `im_validation_violations.details`.
 */
export const ValidationViolationDetailsSchema = z.object({
  observed: ValidationObservedValuesSchema,
  /** Representative rows, spread across the observed values. */
  rows: z.array(ValidationViolationRowSchema),
  /** True when `rows` is a sample of a larger set. */
  rowsTruncated: z.boolean(),
  outcome: ValidationCorrectionOutcomeSchema,
});

/** What an evaluation left out. Never silently dropped. */
export const ValidationEvaluationTruncationSchema = z.object({
  /** More groups disagreed than the cap; the rest aren't reported. */
  violatingGroups: z.boolean(),
  /** Some violation's rows are a sample rather than the whole set. */
  representativeRows: z.boolean(),
});

/**
 * A division table an evaluation could not read, because it does not have one of
 * the rule's columns. A rule names a logical schema (`as_item`) and is evaluated
 * against every division's physical table mapped to it, so a table missing a
 * column is left out of the scan — reported here rather than dropped silently,
 * which would make a rule covering twelve of fifteen divisions look complete.
 */
export const ValidationSkippedTableSchema = z.object({
  dataSourceLinkId: UUIDSchema,
  /** The division's AS/400 code, e.g. `RD`. */
  divisionCode: z.string(),
  /** Division title as configured, for a reader that wants to name it. */
  dataSourceName: z.string().nullable(),
  /** The rule's columns this table lacks, canonical (upper-cased). */
  missingColumns: z.array(z.string()),
});

/** One violation as the evaluator reports it, before it is ever persisted. */
export const ValidationEvaluatedViolationSchema = z.object({
  groupKeyValues: ValidationGroupKeyValuesSchema,
  /** The field that disagreed, canonical (upper-cased). */
  validatedField: z.string(),
  /** Stable identity of (rule, group, field). */
  violationKey: z.string(),
  observed: ValidationObservedValuesSchema,
  rows: z.array(ValidationViolationRowSchema),
  rowsTruncated: z.boolean(),
  outcome: ValidationCorrectionOutcomeSchema,
});

/** The caps an evaluation ran under, so the UI can say what it isn't showing. */
export const ValidationEvaluationCapsSchema = z.object({
  maxViolatingGroups: z.number().int().positive(),
  maxRowsPerGroup: z.number().int().positive(),
});

/**
 * The part of a rule the evaluator needs — everything that decides what a run
 * finds, and nothing about its lifecycle. Picked from the create input so a
 * preview of an unsaved rule is validated exactly like saving it would be.
 */
export const ValidationRuleDefinitionInputSchema =
  CreateValidationRuleInputSchema.pick({
    schemaName: true,
    rowFilters: true,
    groupingFields: true,
    validatedFields: true,
    normalization: true,
    correctionStrategy: true,
    strategyConfig: true,
  });

/**
 * Preview either a saved rule (`ruleId`) or an unsaved definition (`rule`) —
 * exactly one of the two. The unsaved form is what the rule builder previews
 * before the author commits to saving anything.
 */
export const PreviewValidationRuleInputSchema = z
  .object({
    ruleId: UUIDSchema.optional(),
    rule: ValidationRuleDefinitionInputSchema.optional(),
  })
  .refine(
    (input) => (input.ruleId === undefined) !== (input.rule === undefined),
    {
      message:
        "Provide either a saved rule id or an unsaved rule definition, not both",
    },
  );

export const PreviewValidationRuleResponseSchema = z.object({
  stats: ValidationRunStatsSchema,
  truncated: ValidationEvaluationTruncationSchema,
  skippedTables: z.array(ValidationSkippedTableSchema),
  violations: z.array(ValidationEvaluatedViolationSchema),
  caps: ValidationEvaluationCapsSchema,
});

/**
 * What one run changed about the rule's violations. The counts a reviewer cares
 * about: only `newViolations`, `changedViolations` and `recurredViolations` are
 * fresh work — an unchanged violation is one already on someone's list.
 */
export const ValidationRunDeltaSchema = z.object({
  newViolations: z.number().int().nonnegative(),
  changedViolations: z.number().int().nonnegative(),
  recurredViolations: z.number().int().nonnegative(),
  unchangedViolations: z.number().int().nonnegative(),
  resolvedViolations: z.number().int().nonnegative(),
  /**
   * True when the evaluation truncated its violating groups, so violations it
   * did not report were left open instead of being resolved.
   */
  resolutionSkipped: z.boolean(),
});

export const RunValidationRuleInputSchema = z.object({ ruleId: UUIDSchema });

export const RunValidationRuleResponseSchema = z.object({
  runId: UUIDSchema,
  /** `failed` is a recorded outcome, not a transport error — read `error`. */
  status: z.nativeEnum(ValidationRunStatus),
  stats: ValidationRunStatsSchema,
  /** The review the run's corrections were staged into, if any. */
  changeSetId: UUIDSchema.nullable(),
  error: z.string().nullable(),
  truncated: ValidationEvaluationTruncationSchema,
  skippedTables: z.array(ValidationSkippedTableSchema),
  delta: ValidationRunDeltaSchema,
});

export const RegenerateValidationReviewInputSchema = z.object({
  ruleId: UUIDSchema,
});

/**
 * A regeneration is a run that opens a FRESH review and stages every open
 * violation it sees into it — exempted ones left out — instead of appending new
 * work to the rule's open draft. The previous review is not touched: manual
 * edits made on it can be copied over, and it is deleted by hand when done.
 * `changeSetId` is the fresh review (null when nothing needed staging).
 */
export const RegenerateValidationReviewResponseSchema =
  RunValidationRuleResponseSchema;

/** One row of a rule's run history. */
export const ValidationRunSchema = z.object({
  id: UUIDSchema,
  ruleId: UUIDSchema,
  status: z.nativeEnum(ValidationRunStatus),
  triggeredBy: z.nativeEnum(ValidationRuleTrigger),
  /** ISO timestamp. */
  startedAt: z.string(),
  /** Null while the run is still going. */
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
  stats: ValidationRunStatsSchema,
  /** Division tables this run could not read — empty for most runs. */
  skippedTables: z.array(ValidationSkippedTableSchema),
  changeSetId: UUIDSchema.nullable(),
  createdByUserId: UUIDSchema.nullable(),
});

export const ListValidationRunsInputSchema = z.object({
  ruleId: UUIDSchema,
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const ListValidationRunsResponseSchema = z.object({
  /** Newest first. */
  runs: z.array(ValidationRunSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

/** One persisted violation, with the disagreement it is about. */
export const ValidationViolationSchema = z.object({
  id: UUIDSchema,
  ruleId: UUIDSchema,
  violationKey: z.string(),
  status: z.nativeEnum(ValidationViolationStatus),
  groupKeyValues: ValidationGroupKeyValuesSchema,
  validatedField: z.string(),
  details: ValidationViolationDetailsSchema,
  firstSeenRunId: UUIDSchema,
  lastSeenRunId: UUIDSchema,
  resolvedAt: z.string().nullable(),
  /**
   * When a person ruled this disagreement acceptable. The stamps survive
   * resolution (a recurrence comes back exempted), and only removing the
   * exemption clears them.
   */
  exemptedAt: z.string().nullable(),
  exemptedByUserId: UUIDSchema.nullable(),
  exemptionReason: z.string().nullable(),
  changeSetId: UUIDSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * At least one of `ruleId` / `runId` is required — an org-wide violation dump is
 * neither a page the UI has nor an index the table has. `runId` means "the
 * violations that run saw" (`last_seen_run_id`), so a violation that run
 * resolved is not among them.
 */
export const ListValidationViolationsInputSchema = z
  .object({
    ruleId: UUIDSchema.optional(),
    runId: UUIDSchema.optional(),
    status: z.nativeEnum(ValidationViolationStatus).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((input) => input.ruleId !== undefined || input.runId !== undefined, {
    message: "A rule id or a run id is required",
  });

export const ListValidationViolationsResponseSchema = z.object({
  violations: z.array(ValidationViolationSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

/**
 * Rule one violation acceptable, or put it back in play. Exempting takes an
 * optional reason shown wherever the exemption is; removing the exemption
 * reopens a currently-exempted violation.
 */
export const SetViolationExemptionInputSchema = z.object({
  violationId: UUIDSchema,
  exempted: z.boolean(),
  /** Why this disagreement is acceptable. Read only when `exempted` is true. */
  reason: z.string().trim().max(2000).optional(),
});

export const SetViolationExemptionResponseSchema = z.object({
  violation: ValidationViolationSchema,
});

/**
 * The rows a violation card wants the complete ingest record for — its
 * representative rows, addressed exactly as {@link ValidationViolationRowSchema}
 * carries them. Capped at one card's worth (the evaluator samples at most 50
 * rows per group): the card asks for its own rows, never a page of violations
 * at once.
 */
export const GetViolationRowDataInputSchema = z.object({
  rows: z
    .array(z.object({ dataSourceLinkId: UUIDSchema, dataId: z.string() }))
    .min(1)
    .max(50),
});

/**
 * One requested row with every column of its ingest record, read live. Cells
 * come back as text (ingest columns are mixed-typed); `values` is null when the
 * row is gone or its table cannot be read — stale identities are reported, not
 * dropped, so the card can say which of its rows no longer resolve.
 */
export const ViolationFullRowSchema = z.object({
  dataSourceLinkId: UUIDSchema,
  dataId: z.string(),
  divisionCode: z.string(),
  /** The table's columns in their physical order, canonical (upper-cased). */
  columns: z.array(z.string()),
  /** Canonical column name → cell text; a NULL cell is null. */
  values: z.record(z.string(), z.string().nullable()).nullable(),
});

export const GetViolationRowDataResponseSchema = z.object({
  /** In request order. */
  rows: z.array(ViolationFullRowSchema),
});

export const validationRulesContracts = {
  list: oc
    .input(ListValidationRulesInputSchema)
    .output(ListValidationRulesResponseSchema)
    .route({
      method: "GET",
      summary: "List catalog validation rules",
      description:
        "Returns the organization's product catalog validation rules, optionally filtered by status, with each rule's last run and open violation counts.",
    }),
  get: oc
    .input(GetValidationRuleInputSchema)
    .output(GetValidationRuleResponseSchema)
    .route({
      method: "GET",
      summary: "Get a catalog validation rule",
      description:
        "Returns one validation rule: its catalog schema, grouping and validated fields, normalization, correction strategy, schedule and status.",
    }),
  create: oc
    .input(CreateValidationRuleInputSchema)
    .output(CreateValidationRuleResponseSchema)
    .route({
      method: "POST",
      summary: "Create a catalog validation rule",
      description:
        "Creates a rule requiring chosen fields to agree within groups of ERP catalog records, with normalization, correction strategy and optional schedule. Defaults to draft.",
    }),
  update: oc
    .input(UpdateValidationRuleInputSchema)
    .output(UpdateValidationRuleResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update a catalog validation rule",
      description:
        "Edits a validation rule's definition, schedule, correction strategy or status, such as activating or pausing it.",
    }),
  duplicate: oc
    .input(DuplicateValidationRuleInputSchema)
    .output(DuplicateValidationRuleResponseSchema)
    .route({
      method: "POST",
      summary: "Duplicate a catalog validation rule",
      description: 'Copies a validation rule as a new draft named "Copy of …".',
    }),
  delete: oc
    .input(DeleteValidationRuleInputSchema)
    .output(DeleteValidationRuleResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a catalog validation rule",
      description: "Deletes a product catalog validation rule.",
    }),
  listRuleSchemas: oc
    .input(z.object({}))
    .output(ListValidationRuleSchemasResponseSchema)
    .route({
      method: "GET",
      summary: "List catalog schemas for validation rules",
      description:
        "Returns the ERP catalog record schemas the organization can scan and their fields, for building validation rules.",
    }),
  preview: oc
    .input(PreviewValidationRuleInputSchema)
    .output(PreviewValidationRuleResponseSchema)
    // POST because the input carries a whole (possibly unsaved) rule
    // definition; the call itself writes nothing.
    .route({
      method: "POST",
      summary: "Preview a catalog validation rule's results",
      description:
        "Evaluates a saved or unsaved rule definition against current catalog data and returns the violations it finds, without saving anything.",
    }),
  runNow: oc
    .input(RunValidationRuleInputSchema)
    .output(RunValidationRuleResponseSchema)
    .route({
      method: "POST",
      summary: "Run a catalog validation rule now",
      description:
        "Runs a validation rule immediately, records the run, reconciles its violations and stages new findings into the rule's open draft review.",
    }),
  regenerateReview: oc
    .input(RegenerateValidationReviewInputSchema)
    .output(RegenerateValidationReviewResponseSchema)
    .route({
      method: "POST",
      summary: "Regenerate a validation rule's review",
      description:
        "Runs the rule and stages every open violation into a brand-new review change set, leaving the previous review untouched.",
    }),
  listRuns: oc
    .input(ListValidationRunsInputSchema)
    .output(ListValidationRunsResponseSchema)
    .route({
      method: "GET",
      summary: "List a validation rule's run history",
      description:
        "Paged runs of a validation rule, newest first, with status, trigger and records, groups and violations counted.",
    }),
  listViolations: oc
    .input(ListValidationViolationsInputSchema)
    .output(ListValidationViolationsResponseSchema)
    .route({
      method: "GET",
      summary: "List catalog validation violations",
      description:
        "Paged violations for a rule and/or a specific run, optionally by status, with the disagreeing values and any proposed correction.",
    }),
  setViolationExemption: oc
    .input(SetViolationExemptionInputSchema)
    .output(SetViolationExemptionResponseSchema)
    .route({
      method: "PATCH",
      summary: "Exempt a catalog validation violation",
      description:
        "Marks a violation as an accepted exception so it stops counting as open, or removes that exemption.",
    }),
  getViolationRowData: oc
    .input(GetViolationRowDataInputSchema)
    .output(GetViolationRowDataResponseSchema)
    // POST because the input is a list of row identities; the call reads only.
    .route({
      method: "POST",
      summary: "Get catalog records behind a violation",
      description:
        "Returns the complete ERP catalog records behind a validation violation's representative rows.",
    }),
} as const;

export type ValidationNormalization = z.infer<
  typeof ValidationNormalizationSchema
>;
export type ValidationStrategyConfig = z.infer<
  typeof ValidationStrategyConfigSchema
>;
export type ValidationRunStats = z.infer<typeof ValidationRunStatsSchema>;
export type ValidationRuleDTO = z.infer<typeof ValidationRuleSchema>;
export type ValidationRuleLastRun = z.infer<typeof ValidationRuleLastRunSchema>;
export type ValidationRuleListItemDTO = z.infer<
  typeof ValidationRuleListItemSchema
>;
export type ListValidationRulesInput = z.infer<
  typeof ListValidationRulesInputSchema
>;
export type ListValidationRulesResponse = z.infer<
  typeof ListValidationRulesResponseSchema
>;
export type GetValidationRuleResponse = z.infer<
  typeof GetValidationRuleResponseSchema
>;
export type CreateValidationRuleInput = z.infer<
  typeof CreateValidationRuleInputSchema
>;
export type CreateValidationRuleResponse = z.infer<
  typeof CreateValidationRuleResponseSchema
>;
export type UpdateValidationRuleInput = z.infer<
  typeof UpdateValidationRuleInputSchema
>;
export type UpdateValidationRuleResponse = z.infer<
  typeof UpdateValidationRuleResponseSchema
>;
export type DuplicateValidationRuleResponse = z.infer<
  typeof DuplicateValidationRuleResponseSchema
>;
export type DeleteValidationRuleResponse = z.infer<
  typeof DeleteValidationRuleResponseSchema
>;
export type ValidationSchemaFieldRole = z.infer<
  typeof ValidationSchemaFieldRoleSchema
>;
export type ValidationSchemaField = z.infer<typeof ValidationSchemaFieldSchema>;
export type ValidationRuleSchemaDTO = z.infer<
  typeof ValidationRuleSchemaSchema
>;
export type ListValidationRuleSchemasResponse = z.infer<
  typeof ListValidationRuleSchemasResponseSchema
>;
export type ValidationGroupKeyValues = z.infer<
  typeof ValidationGroupKeyValuesSchema
>;
export type ValidationObservedDivision = z.infer<
  typeof ValidationObservedDivisionSchema
>;
export type ValidationObservedValue = z.infer<
  typeof ValidationObservedValueSchema
>;
export type ValidationObservedValues = z.infer<
  typeof ValidationObservedValuesSchema
>;
export type ValidationCorrectionOutcome = z.infer<
  typeof ValidationCorrectionOutcomeSchema
>;
export type ValidationViolationRow = z.infer<
  typeof ValidationViolationRowSchema
>;
/** The shape of `im_validation_violations.details`. */
export type ValidationViolationDetailsDTO = z.infer<
  typeof ValidationViolationDetailsSchema
>;
export type ValidationEvaluationTruncation = z.infer<
  typeof ValidationEvaluationTruncationSchema
>;
export type ValidationSkippedTable = z.infer<
  typeof ValidationSkippedTableSchema
>;
export type ValidationEvaluatedViolationDTO = z.infer<
  typeof ValidationEvaluatedViolationSchema
>;
export type ValidationEvaluationCaps = z.infer<
  typeof ValidationEvaluationCapsSchema
>;
export type ValidationRuleDefinitionInput = z.infer<
  typeof ValidationRuleDefinitionInputSchema
>;
export type PreviewValidationRuleInput = z.infer<
  typeof PreviewValidationRuleInputSchema
>;
export type PreviewValidationRuleResponse = z.infer<
  typeof PreviewValidationRuleResponseSchema
>;
export type ValidationRunDelta = z.infer<typeof ValidationRunDeltaSchema>;
export type RunValidationRuleInput = z.infer<
  typeof RunValidationRuleInputSchema
>;
export type RunValidationRuleResponse = z.infer<
  typeof RunValidationRuleResponseSchema
>;
export type RegenerateValidationReviewInput = z.infer<
  typeof RegenerateValidationReviewInputSchema
>;
export type RegenerateValidationReviewResponse = z.infer<
  typeof RegenerateValidationReviewResponseSchema
>;
export type ValidationRunDTO = z.infer<typeof ValidationRunSchema>;
export type ListValidationRunsInput = z.infer<
  typeof ListValidationRunsInputSchema
>;
export type ListValidationRunsResponse = z.infer<
  typeof ListValidationRunsResponseSchema
>;
export type ValidationViolationDTO = z.infer<typeof ValidationViolationSchema>;
export type ListValidationViolationsInput = z.infer<
  typeof ListValidationViolationsInputSchema
>;
export type ListValidationViolationsResponse = z.infer<
  typeof ListValidationViolationsResponseSchema
>;
export type SetViolationExemptionInput = z.infer<
  typeof SetViolationExemptionInputSchema
>;
export type SetViolationExemptionResponse = z.infer<
  typeof SetViolationExemptionResponseSchema
>;
export type GetViolationRowDataInput = z.infer<
  typeof GetViolationRowDataInputSchema
>;
export type ViolationFullRow = z.infer<typeof ViolationFullRowSchema>;
export type GetViolationRowDataResponse = z.infer<
  typeof GetViolationRowDataResponseSchema
>;
